import { format } from 'date-fns'
import { Button, Collapse, Grid, IconButton, Stack } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import DownloadIcon from '@mui/icons-material/Download'
import styled from 'styled-components'

import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootStore, useAppDispatch } from '../../../../redux/store'
import actionsService from '../../../../services/actionsService'

import theme from '../../../../theme/theme'
import HolidayEntitlementBulgariaSearchForm from './HolidayEntitlementBulgariaSearchForm'
import { Search, HolidaysEntitlementBulgariaCSV } from './types'
import HolidayEntitlementBulgariaGrid from './HolidayEntitlementBulgariaGrid'
import LoadingIndicator from '../../../../shared/UI/LoadingIndicator'
import { BaseResponse } from '../../../../types/base-response'
import { showErrorMessage } from '../../../../redux/reducers/snackbarReducer'
import UserErrorMessage from '../../../../utils/errorFilter'
import { DrawerComponentProps } from '../../types'
import Drawer from '../../../../shared/UI/Drawer'
import { ssrsHolidayReportService } from '../../../../services/ssrsHolidayReportService'
import {
  HolidayEntitlementBulgariaItem,
  HolidayEntitlementBulgariaResponse,
} from '../../../../types/holiday-entitlement-bulgaria-response'
import HolidayEntitlementBulgariaDetail from './HolidayEntitlementBulgariaDetail'
import RefreshButton from '../../../../shared/UI/RefreshButton'
import DownloadButton from '../../../../shared/UI/DownloadButton'
import FilterButton from '../../../../shared/UI/FilterButton'

export const LoadingIndicatorContainer = styled.div`
  padding-top: 70px;
  padding-bottom: 70px;
`

function HolidayEntitlementBulgariaSearch() {
  const dispatch = useAppDispatch()
  const [resetForm, setResetForm] = useState(false)
  const [showHeaderButtons, setShowHeaderButtons] = useState<boolean>(false)
  const [showSearch, setShowSearch] = useState<boolean>(true)
  const [showGrid, setShowGrid] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [data, setData] = useState<HolidayEntitlementBulgariaItem[]>([])
  const [openDrawer, setOpenDrawer] = useState(false)

  const { currentEntitlementPeriodResponse, departments } = useSelector(
    (state: RootStore) => state.appSettings
  )

  const handleClose = () => {
    setOpenDrawer(false)
  }

  const [drawerComponent, setDrawerComponent] = useState<DrawerComponentProps>({
    title: '',
    component: <HolidayEntitlementBulgariaDetail row={null} onClose={handleClose} />,
  })

  const handleSearch = (search: Search) => {
    setIsLoading(true)
    setShowGrid(false)

    setTimeout(() => {
      setShowSearch(false)
    }, 500)

    ssrsHolidayReportService
      .getHolidaysEntitlementBulgariaReports(
        search.fromDateRangeFrom !== ''
          ? search.fromDateRangeFrom!
          : format(new Date(currentEntitlementPeriodResponse!.startDate), 'yyyy-MM-dd')
      )
      .then((res: HolidayEntitlementBulgariaResponse) => {
        const result: HolidayEntitlementBulgariaItem[] = res.rows.map(
          (row: HolidayEntitlementBulgariaItem, index: number) => {
            const holidayRemaining: number = row.holidayAllowance! - row.holidayTaken!

            let holidayTakenPc = 0
            if (row.holidayAllowance !== 0) {
              holidayTakenPc = (row.holidayTaken! / row.holidayAllowance!) * 100
            }
            return {
              ...row,
              id: index,
              holidayRemaining,
              holidayTakenPc: holidayTakenPc!.toFixed(2),
            }
          }
        )

        setData(
          result.sort((a, b) =>
            a.displayName!.toLowerCase() > b.displayName!.toLowerCase() ? 1 : -1
          )
        )
        setShowHeaderButtons(true)
        setShowGrid(true)
        setIsLoading(false)
      })
      .catch(err => {
        setShowHeaderButtons(true)
        setShowGrid(false)
        setIsLoading(false)
        setTimeout(() => {
          setShowSearch(true)
        }, 500)

        const response: BaseResponse = err.response.data
        if (response.errors) {
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        } else {
          dispatch(showErrorMessage(<UserErrorMessage name="CurrentlyUnavailable" />))
        }
      })
  }

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    actionsService.userAnalytics
      .postEvent('UIClickEvent', 'HolidayReportsEntitlementBulgariaChangeFiltersClick', window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    setShowSearch(!showSearch)
  }

  const handleRefreshClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    actionsService.userAnalytics
      .postEvent('UIClickEvent', 'HolidayReportsEntitlementBulgariaRefreshClick', window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    setResetForm(!resetForm)

    setShowSearch(true)
    setShowHeaderButtons(false)
    setShowGrid(false)
  }

  const handleViewDetail = (row: any) => {
    setDrawerComponent({
      ...drawerComponent,
      title: `${row.displayName}`,
      component: <HolidayEntitlementBulgariaDetail row={row} onClose={handleClose} />,
    })

    setOpenDrawer(true)
  }

  const makeCsv = async (filename: string) => {
    const headers: string[] = [
      'EmployeeName',
      'JobTitle',
      'Team',
      'HolidayEntitlement',
      'HolidayEntitlementTaken',
      'HolidayEntitlementRemaining',
      'HolidayTakenPc',
    ]

    const csvData: HolidaysEntitlementBulgariaCSV[] = data.map(
      (row: HolidayEntitlementBulgariaItem) => {
        const item: HolidaysEntitlementBulgariaCSV = {
          EmployeeName: row.displayName!,
          JobTitle: row.position,
          Team: row.team,
          HolidayEntitlement: row.holidayAllowance,
          HolidayEntitlementTaken: row.holidayTaken,
          HolidayEntitlementRemaining: row.holidayRemaining,
          HolidayTakenPc: row.holidayTakenPc,
        }
        return item
      }
    )

    const keys: string[] = Object.keys(csvData[0])

    const csvContent: string = csvData
      .map((row: any) =>
        keys
          .map(k => {
            let cell = row[k] === null || row[k] === undefined ? '' : row[k]

            cell =
              cell instanceof Date ? cell.toLocaleString(navigator.language) : cell.toString().replace(/"/g, '""')

            if (cell.search(/("|,|\n)/g) >= 0) {
              cell = `"${cell}"`
            }
            return cell
          })
          .join(',')
      )
      .join('\n')

    const csvHeaders: string = headers.join(',')

    const blob = new Blob([`${csvHeaders}\n`, csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')

    if (link.download !== undefined) {
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    actionsService.userAnalytics
      .postEvent('UIClickEvent', 'HolidayReportsEntitlementBulgariaDownloadClick', window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    makeCsv('HolidayEntitlementBulgaria.csv')
  }

  return (
    <>
      {showHeaderButtons && (
        <Grid container style={{ border: '0px red solid' }}>
          <Stack
            direction="row"
            style={{ border: '0px green solid', width: '100%' }}
            spacing={2}
            display="flex"
            justifyContent="flex-end"
          >
            <FilterButton onClick={handleFilterClick} title="Change Filters" />
            {data && data.length >= 1 && (
              <DownloadButton onClick={handleDownloadClick} title="Download Report" />
            )}
            <RefreshButton onClick={handleRefreshClick} title="Refresh" />
          </Stack>
        </Grid>
      )}
      <Collapse orientation="vertical" in={showSearch}>
        <HolidayEntitlementBulgariaSearchForm resetForm={resetForm} onSearch={handleSearch} searchLoading={isLoading} />
      </Collapse>
      <Grid item xs={12}>
        {!isLoading && showGrid && (
          <HolidayEntitlementBulgariaGrid data={data} onViewDetail={handleViewDetail} />
        )}
        {isLoading && (
          <LoadingIndicatorContainer>
            <LoadingIndicator show={isLoading} />
          </LoadingIndicatorContainer>
        )}
      </Grid>
      <Drawer
        isOpen={openDrawer}
        onClose={() => {
          setOpenDrawer(false)
        }}
        title={drawerComponent?.title || ''}
        showOptions={false}
      >
        {drawerComponent?.component}
      </Drawer>
    </>
  )
}

export default HolidayEntitlementBulgariaSearch
